import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateAccountTransactionType,
  useEditAccountTransactionType,
} from "hooks/useAccountTransactionType"

const AccountTransactionTypeAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountTransactionType()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountTransactionType()

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter transaction type name"),
    slug: Yup.string()
      .trim()
      .required("Please enter transaction type slug")
      .matches(/^[a-z0-9-]+$/, "Slug can contain lowercase letters, numbers, and hyphen"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name?.trim(),
        slug: values.slug?.trim(),
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Transaction type ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        toast.error("Failed to save transaction type.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      slug: editedData?.slug || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Transaction Type"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Transaction Type" : "Add Transaction Type"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">Name</Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <Label htmlFor="slug">Slug</Label>
          <InputField
            type="text"
            name="slug"
            id="slug"
            value={formik.values.slug}
            onChange={formik.handleChange}
            invalid={formik.touched.slug && !!formik.errors.slug}
          />
          {formik.touched.slug && formik.errors.slug && (
            <FormFeedback type="invalid">{formik.errors.slug}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountTransactionTypeAddEdit
